import { selectVehicle } from "action/garage/selectVehicle";
import { closeModal } from "action/modal/closeModal";
import { IBucket } from "api/interfaces/product-api.type";
import classnames from "classnames";
import Checkbox from "component/design-system/checkbox/Checkbox";
import PAText from "component/design-system/typography/PAText/PAText";
import { tagColorMap } from "component/product/product-tags/RenderTag";
import { RootState } from "interfaces/generics.type";
import { WHITELIST_PARAMS, validQueryParams } from "modules/category/constants/constants";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import qs from "query-string";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { trackFitmentEvent } from "util/analytics/trackFitmentEvent";
import { TRACKING_EVENT_NAME } from "util/analytics/tracking.service";
import yieldAnalyticsEvent from "util/analytics/yieldAnalyticsEvent";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import { isFunction } from "util/helper/isFunction";
import { pick } from "util/lodash/pick";
import { colorMap } from "util/product/apparel";
import { getHrefAndPath } from "util/url/getHrefAndPath";
import FilterDropdown from "../Dropdown/FilterDropdown";
import FilterSidebarMenu from "../FilterSidebarMenu";
import styles from "./product-checkbox-filter.module.scss";
const CategoryFitmentOption = dynamic(() => import(/* webpackChunkName: "category-fitment-option" */ "component/category/category-fitment-option/CategoryFitmentOption"));

function ProductCheckBoxFilter(this: any, props: any) {
  const {
    title, filterKey, multiple,
    className, itemValue = (item: any) => item.bucket, appliedFilters, items, url,
    itemLabel = (item: any) => item.bucket,
    isModal, isFitmentAttribute, icon, customDropdown,
  } = props;

  const [searchText, setSearchText] = useState("");
  const dispatch = useDispatch();

  const selectedVehicle = useSelector((state: RootState) => state.garage.selectedVehicle);
  const { selectedId } = useSelector((store: RootState) => store.modal);
  const { urlInformation } = useSelector((store: RootState) => store.utils);
  const router = useRouter();
  const { query } = router;
  const { t } = useTranslation("category");
  const listItemRef = useRef(null);

  function getFilterItems() {
    if (searchText.length !== 0) {
      return items.filter((each: any) => each.bucket.toLowerCase().indexOf(searchText.toLowerCase()) !== -1);
    }
    if (filterKey === "productTags") {
      return items.map((item) => {
        const color = tagColorMap(item.bucket);
        return { ...item, color };
      });
    }

    return items;
  }

  const filterItems = getFilterItems();

  const selectedItems = [].concat(appliedFilters[filterKey] || []);
  const TitleComponent = title;
  const renderedTitle = isFunction(title)
    ? <TitleComponent {...{ filterKey, selectedItems }} />
    : t(title);

  function onSearchHandler(value: any) {
    // remove special char from search text
    setSearchText(value.replace(/[^a-zA-Z0-9]/g, ""));
  }

  function handleClickItem(item: IBucket) {
    const _selectedItems = [].concat(appliedFilters[filterKey] || []);
    const value = itemValue(item);
    let finalItems = [];
    if (multiple) {
      finalItems = _selectedItems.slice(0);
      const index = finalItems.indexOf(value);
      if (index !== -1) {
        finalItems.splice(index, 1);
      } else {
        finalItems.push(value);
      }
    } else {
      finalItems = value;
    }
    if (isFitmentAttribute && selectedVehicle) {
      // change in engine, its same as change of vehicle change work flow.
      const { fitmentOptions } = selectedVehicle;
      const fitmentIdx = fitmentOptions.findIndex((fo) => fo.optionName === filterKey);
      dispatch(selectVehicle({
        ...selectedVehicle,
        fitmentOptions: fitmentIdx === -1 ? [...fitmentOptions, { optionName: filterKey, optionValue: item.bucket }]
          : [...fitmentOptions.slice(0, fitmentIdx),
            { optionName: filterKey, optionValue: item.bucket },
            ...fitmentOptions.slice(fitmentIdx + 1)],
      }));
      if (selectedId) {
        dispatch(closeModal());
      }
    } else {
      const allQuery = pick(process.browser ? qs.parse(location.search) : query, validQueryParams);
      const queryParams = {
        sizes: query.sizes, // for tires size pages i.e /tires/205-55R16
        ...allQuery,
        pathname: undefined,
        [filterKey]: finalItems,
        // scroll: undefined,
        pageNumber: undefined,
      };
      const queryString = qs.stringify(queryParams);

      const { href, asPath } = getHrefAndPath({ queryString, href: url, asPath: url === "/tires" ? url : query.pathname });
      router.push(href, asPath, { scroll: false });
    }
  }

  function clearFitmentOption(FitmentToRemove: string) {
    const newFitmentOption = selectedVehicle?.fitmentOptions.filter((_) => _.optionName !== FitmentToRemove);
    const clearedFitmentOption = selectedVehicle?.fitmentOptions.find((fo) => fo.optionName === FitmentToRemove);
    if (selectedVehicle?.fitmentOptions.length === newFitmentOption.length) {
      // fitment option already deselected
      return;
    }

    const {
      tier0, tier1, tier2, tier3,
    } = urlInformation;
    yieldAnalyticsEvent(() => trackFitmentEvent(TRACKING_EVENT_NAME.FITMENT_OPTION_CLEARED, {
      fitmentOptionName: clearedFitmentOption?.optionName,
      fitmentOptionValue: clearedFitmentOption?.optionValue,
      selectedFitments: selectedVehicle.fitmentOptions.map((fo) => fo.optionName),
      tier0,
      tier1,
      tier2,
      tier3,
    }));

    dispatch(selectVehicle({
      ...selectedVehicle,
      fitmentOptions: newFitmentOption,
    }));
  }

  function isChecked(item: IBucket) {
    if (isFitmentAttribute && selectedVehicle) {
      return selectedVehicle.fitmentOptions.some((fo) => fo.optionName === filterKey && fo.optionValue === item.bucket);
    }
    const selectedFilters = appliedFilters[filterKey];
    let _selectedItems = selectedFilters;
    if (_selectedItems && Array.isArray(_selectedItems)) {
      _selectedItems = _selectedItems.map((el) => el.toLowerCase());
    } else if (_selectedItems) {
      _selectedItems = _selectedItems.toLowerCase();
    }
    const value = itemValue(item).toLowerCase();
    let checked = false;
    if (!process.browser && !WHITELIST_PARAMS.includes(filterKey)) {
      return false;
    }
    if (_selectedItems) {
      if (Array.isArray(_selectedItems) && _selectedItems.indexOf(value) !== -1) {
        checked = true;
      } else if (!Array.isArray(_selectedItems) && _selectedItems === value) {
        checked = true;
      }
    }
    return checked;
  }

  function renderList() {
    if (["merchandiseColors", "airFreshenerColors"].includes(filterKey)) {
      return (
        <div className={styles.colors_container}>
          {filterItems.map((item: any) => (
            <div
              key={item.bucket}
              className={styles.color_box}
            >
              <div
                className={`${styles.color_circle} ${isChecked(item) && styles.color_selected }`}
                onClick={() => handleClickItem(item)}
              >
                <Image
                  className={styles.color_circle}
                  src={getRelativeImagePath(colorMap[item.bucket])}
                  width={50}
                  height={50}
                />
              </div>
              <PAText className={styles.color_name}>
                {item.bucket}
              </PAText>
            </div>
          ))}
        </div>
      );
    }
    return (
      <ul className="list list-unstyled" data-testid={filterKey}>
        {items.length > 8 && (
          <input
            type="text"
            className={styles.search_filter}
            placeholder={t("Search..")}
            onChange={(e) => onSearchHandler(e.target.value)}
            // Stop event firing when clicked on search otherwise it closes dropdown
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <div ref={listItemRef}>
          {
            filterItems
              .map((item: any, index: any) => (
                <li
                  key={index}
                >
                  <Checkbox
                    isAfterLabel
                    checked={isChecked(item)}
                    label={itemLabel(item)}
                    radio={!multiple}
                    onChange={() => handleClickItem(item)}
                    id="filter-checkbox"
                  />
                </li>
              ))
          }
          <HideIf show={isFitmentAttribute}>
            <div
              onClick={() => clearFitmentOption(filterKey)}
              className={styles.clearAll}
            >
              Clear
            </div>
          </HideIf>
        </div>
      </ul>
    );
  }

  function isFilterExpanded() {
    return filterItems.some((item) => isChecked(item));
  }

  if (isModal) {
    const isDefaultexpanded = isFilterExpanded();
    return (
      <FilterSidebarMenu
        filterKey={filterKey}
        title={renderedTitle}
        renderList={renderList}
        isDefaultexpanded={isDefaultexpanded}
      />
    );
  }

  if (customDropdown && selectedVehicle) {
    return (
      <CategoryFitmentOption
        filterKey={filterKey}
        itemValue={itemValue}
        itemLabel={itemLabel}
        icon={icon}
        title={renderedTitle}
        ref={listItemRef}
      >
        {renderList()}
      </CategoryFitmentOption>
    );
  }

  return (
    <div className={classnames(styles.product_checkbox_filter, { [className]: className }, "font-16px")}>
      <FilterDropdown
        title={renderedTitle}
        index={props.index}
        icon={icon}
        filterKey={filterKey}
        setSearchText={setSearchText}
      >
        {renderList()}
      </FilterDropdown>
    </div>
  );
}

export default ProductCheckBoxFilter;
